import { useState, type FormEvent } from 'react';

import { Button } from '@/shared/ui/Button';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';

import type { Client, ClientCreate } from './types';

/**
 * The fields of a client, as the quote prints them.
 *
 * The order is the order of the document (contexto §4.6): who they are, where they are,
 * and how they pay. It is not alphabetical and it is not "required first", because the
 * person filling this in is usually copying from a quote or an invoice and reading top to
 * bottom.
 *
 * When the form has room (the wide sheet on a desktop) the blocks sit in two columns (who
 * and how to reach them on the left, where and how they pay on the right) so the whole
 * card fits without scrolling; in a narrow box or on a phone they stack. Everything except
 * the name is optional, on purpose. A client typed in a hurry to get a quote out is better
 * than a quote that cannot be made because nobody has the province.
 */
const EMPTY: ClientCreate = {
  name: '',
  trade_name: null,
  code: null,
  nif: null,
  street: null,
  postal_code: null,
  city: null,
  province: null,
  representative_code: null,
  payment_terms: null,
  email: null,
  phone: null,
  notes: null,
  is_active: true,
};

/**
 * The values the form starts with.
 *
 * Read once, in `useState`'s initialiser. That is why the caller has to give this
 * component a `key` tied to the client it is editing: without one, React reconciles the
 * same instance when the editor is pointed at another row, the initialiser does not run
 * again, and the form ends up showing client A while it saves to client B.
 */
function initial(client: Client | undefined): ClientCreate {
  if (!client) return EMPTY;
  return {
    name: client.name,
    trade_name: client.trade_name ?? null,
    code: client.code ?? null,
    nif: client.nif ?? null,
    street: client.street ?? null,
    postal_code: client.postal_code ?? null,
    city: client.city ?? null,
    province: client.province ?? null,
    representative_code: client.representative_code ?? null,
    payment_terms: client.payment_terms ?? null,
    email: client.email ?? null,
    phone: client.phone ?? null,
    notes: client.notes ?? null,
    is_active: client.is_active ?? true,
  };
}

type TextField = Exclude<keyof ClientCreate, 'is_active' | 'usual_discount_pct' | 're_applies'>;

const FIELDS: Record<TextField, { label: string; hint?: string }> = {
  name: { label: 'Razón social' },
  trade_name: { label: 'Nombre comercial', hint: 'El de la tienda, si se le conoce por él.' },
  code: { label: 'Código de cliente', hint: 'El de Profer. Es la clave: no se repite.' },
  nif: { label: 'NIF', hint: 'Puede repetirse entre fichas de una misma sociedad.' },
  street: { label: 'Dirección' },
  postal_code: { label: 'Código postal' },
  city: { label: 'Localidad' },
  province: { label: 'Provincia' },
  representative_code: { label: 'Representante' },
  payment_terms: { label: 'Forma de pago', hint: 'Transferencia, FACE, giro…' },
  email: { label: 'Correo' },
  phone: { label: 'Teléfono' },
  notes: { label: 'Notas' },
};

/** Trimmed, or `null` when nothing was typed. */
function blank(value: string | null | undefined): string | null {
  return typeof value === 'string' ? value.trim() || null : null;
}

export function ClientForm({
  client,
  saving,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  client?: Client;
  saving: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (payload: ClientCreate) => void;
  onCancel?: () => void;
}) {
  const [values, setValues] = useState<ClientCreate>(() => initial(client));
  const [touched, setTouched] = useState(false);

  const nameMissing = touched && !values.name.trim();

  function submit(event: FormEvent): void {
    event.preventDefault();
    setTouched(true);
    if (!values.name.trim()) return;
    // Written out field by field, and **not** `Object.fromEntries` with a cast. That cast
    // switched off the check against the contract entirely: a field the back renamed or
    // made required would have passed `npm run typecheck` in silence, which is the one
    // thing generating the types from `openapi.json` exists to prevent. This way the
    // compiler is the one that notices.
    //
    // Empty strings become null: the API stores what it is given, and `""` in a NIF
    // column reads as "somebody checked and it is blank" rather than "nobody asked".
    onSubmit({
      name: values.name.trim(),
      trade_name: blank(values.trade_name),
      code: blank(values.code),
      nif: blank(values.nif),
      street: blank(values.street),
      postal_code: blank(values.postal_code),
      city: blank(values.city),
      province: blank(values.province),
      representative_code: blank(values.representative_code),
      payment_terms: blank(values.payment_terms),
      email: blank(values.email),
      phone: blank(values.phone),
      notes: blank(values.notes),
      is_active: values.is_active ?? true,
    });
  }

  function field(name: TextField, className?: string) {
    const meta = FIELDS[name];
    return (
      <div className={className}>
        <Input
          label={meta.label}
          hint={meta.hint}
          value={values[name] ?? ''}
          error={name === 'name' && nameMissing ? 'La razón social es obligatoria' : ''}
          onChange={(event) => setValues((current) => ({ ...current, [name]: event.target.value }))}
        />
      </div>
    );
  }

  return (
    // `@container` and the `@…:` variants: the columns follow the width of the form, not of
    // the screen. With `md:` a form painted in a narrow box on a wide screen (the client card
    // of the quote editor) split into columns it had no room for and the fields overlapped.
    <form onSubmit={submit} className="@container flex flex-col gap-4">
      <div className="grid gap-x-6 gap-y-3 @2xl:grid-cols-2">
        {/* Who they are, and how to reach them. */}
        <div className="grid min-w-0 content-start gap-3">
          {field('name')}
          {field('trade_name')}
          <div className="grid gap-3 @sm:grid-cols-2">
            {field('nif')}
            {field('code')}
          </div>
          <div className="grid gap-3 @sm:grid-cols-2">
            {field('email')}
            {field('phone')}
          </div>
        </div>
        {/* Where they are, and how they pay. */}
        <div className="grid min-w-0 content-start gap-3">
          {field('street')}
          <div className="grid gap-3 @sm:grid-cols-[minmax(0,7rem)_1fr]">
            {field('postal_code')}
            {field('city')}
          </div>
          {field('province')}
          <div className="grid gap-3 @sm:grid-cols-2">
            {field('representative_code')}
            {field('payment_terms')}
          </div>
        </div>
      </div>

      <label className="text-ink-2 flex flex-wrap items-center gap-2 text-[14px]">
        <input
          type="checkbox"
          className="accent-accent size-4"
          checked={values.is_active ?? true}
          onChange={(event) =>
            setValues((current) => ({ ...current, is_active: event.target.checked }))
          }
        />
        Activo
        <span className="text-ink-4 text-[12.5px]">
          De baja no sale en el selector del presupuesto.
        </span>
      </label>

      {error ? <ErrorMessage error={error}>No se pudo guardar el cliente</ErrorMessage> : null}

      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? 'Guardando…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}
